import { FormCriteriaInput } from "@/shared/ui/FormCriteriaInput";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormInput,
  FormRoot,
  FormTextarea,
  Label,
  RadioGroup,
  RadioGroupItem,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import * as React from "react";
import { z } from "zod";
import type { CreateKPIInput, UpdateKPIInput } from "../../domain/contracts";
import { validateCriteriaFormat } from "../utils/criteria-validator";
import { kpiFormSamples } from "./kpiFormSamples";
import { KPIFormSections } from "./KPIFormSections";

const KPIFormSchema = z.object({
  name: z.string().min(1, "ຊື່ KPI ຈຳເປັນຕ້ອງກະລຸນາ").max(200, "ຊື່ KPI ຍາວເກີນໄປ"),
  description: z.string().max(1000, "ຄຳອະທິບາຍຍາວເກີນໄປ").optional().nullable(),
  level: z.enum(["employee", "position", "department", "organization"]),
  measurementMethod: z
    .string()
    .min(1, "ວິທີວັດຈຳເປັນຕ້ອງກະລຸນາ")
    .max(500, "ວິທີວັດຍາວເກີນໄປ"),
  measurementSource: z
    .string()
    .max(200, "ແຫຼ່ງຂໍ້ມູນຍາວເກີນໄປ")
    .optional()
    .nullable(),
  measurementPeriod: z
    .enum(["monthly", "quarterly", "yearly"])
    .default("monthly"),
  targetValue: z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === "number") return val;
      if (typeof val === "string") {
        const trimmed = val.trim();
        if (trimmed === "") return null;
        const num = Number.parseFloat(trimmed);
        return Number.isNaN(num) ? null : num;
      }
      return null;
    }),
  targetUnit: z
    .enum(["percent", "count", "score", "currency", "none"])
    .optional()
    .nullable(),
  passCriteria: z
    .string()
    .max(200, "ກຳນົດຜ່ານຍາວເກີນໄປ")
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true; // Optional field
        const result = validateCriteriaFormat(val);
        return result.isValid && !result.error;
      },
      {
        message:
          'ຮູບແບບບໍ່ຖືກຕ້ອງ. ຕົວຢ່າງ: ">= 90%", "<= 5 ຄັ້ງ", ຫຼືຂໍ້ຄວາມອິດສະຫຼະຢ່າງໜ້ອຍ 5 ຕົວອັກສອນ',
      },
    ),
  failCriteria: z
    .string()
    .max(200, "ກຳນົດບໍ່ຜ່ານຍາວເກີນໄປ")
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true; // Optional field
        const result = validateCriteriaFormat(val);
        return result.isValid && !result.error;
      },
      {
        message:
          'ຮູບແບບບໍ່ຖືກຕ້ອງ. ຕົວຢ່າງ: ">= 90%", "<= 5 ຄັ້ງ", ຫຼືຂໍ້ຄວາມອິດສະຫຼະຢ່າງໜ້ອຍ 5 ຕົວອັກສອນ',
      },
    ),
  notes: z.string().max(1000, "ໝາຍເຫດຍາວເກີນໄປ").optional().nullable(),
  status: z.enum(["active", "draft", "inactive"]).optional(),
});

export type KPIFormValues = z.infer<typeof KPIFormSchema>;

export function KPIForm({
  initialValues,
  onSubmit,
  submitting,
  isEdit = false,
  variant = "modal",
}: {
  initialValues?: Partial<KPIFormValues>;
  onSubmit: (values: CreateKPIInput | UpdateKPIInput) => void;
  submitting?: boolean;
  isEdit?: boolean;
  variant?: "modal" | "page";
}) {
  const [sampleIndex, setSampleIndex] = React.useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<KPIFormValues>({
    resolver: zodResolver(KPIFormSchema) as never,
    defaultValues: {
      name: "",
      description: "",
      level: "employee",
      measurementMethod: "",
      measurementSource: "",
      measurementPeriod: "monthly",
      targetValue: initialValues?.targetValue ?? undefined,
      targetUnit: initialValues?.targetUnit ?? undefined,
      passCriteria: "",
      failCriteria: "",
      notes: "",
      status: "draft",
      ...initialValues,
    },
  });

  const handleAutoFill = () => {
    const sample = kpiFormSamples[sampleIndex];
    if (!sample) return;
    Object.entries(sample).forEach(([key, value]) => {
      methods.setValue(key as keyof KPIFormValues, value as never);
    });
    setSampleIndex((prev) => (prev + 1) % kpiFormSamples.length);
    toast.success("Auto-filled form with sample data");
  };

  const handleFormSubmit = (status?: KPIFormValues["status"]) => {
    const vals = methods.getValues();
    // Convert targetValue to number if it's a string
    // FormNumber may return string from input field, so we need to convert it
    let targetValue: number | null = null;
    const rawTargetValue = vals.targetValue as
      | string
      | number
      | null
      | undefined;
    if (rawTargetValue !== null && rawTargetValue !== undefined) {
      if (typeof rawTargetValue === "number") {
        targetValue = rawTargetValue;
      } else if (typeof rawTargetValue === "string") {
        const trimmed = rawTargetValue.trim();
        if (trimmed !== "") {
          const num = Number.parseFloat(trimmed);
          targetValue = Number.isNaN(num) ? null : num;
        }
      }
    }
    onSubmit({
      name: vals.name,
      description: vals.description || null,
      level: vals.level,
      measurementMethod: vals.measurementMethod,
      measurementSource: vals.measurementSource || null,
      measurementPeriod: vals.measurementPeriod,
      targetValue,
      targetUnit: vals.targetUnit || null,
      passCriteria: vals.passCriteria || null,
      failCriteria: vals.failCriteria || null,
      notes: vals.notes || null,
      ...(isEdit ? { status: vals.status } : { status: status || "draft" }),
    });
  };

  // Page variant: use sections layout
  if (variant === "page") {
    return (
      <FormRoot<KPIFormValues>
        methods={methods}
        onSubmit={() => {
          // Handled by button onClick handlers
        }}
        className="space-y-6"
      >
        <KPIFormSections formMethods={methods} isEdit={isEdit} />

        <div className="flex justify-between border-t pt-6">
          {isDev && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={submitting}
            >
              <SparklesIcon className="mr-2 h-4 w-4" />
              Auto Fill (Sample {sampleIndex + 1}/{kpiFormSamples.length})
            </Button>
          )}
          <div className="flex gap-3" data-tourid="form-save-draft">
            {!isEdit && (
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  methods.handleSubmit(() => {
                    handleFormSubmit("draft");
                  })();
                }}
              >
                ບັນທຶກເປັນຮ່າງ
              </Button>
            )}
          </div>
          <div data-tourid="form-submit">
            {!isEdit ? (
              <Button
                type="button"
                disabled={submitting}
                isLoading={submitting}
                onClick={() => {
                  methods.handleSubmit(() => {
                    handleFormSubmit("active");
                  })();
                }}
              >
                ບັນທຶກແລະເປີດໃຊ້
              </Button>
            ) : (
              <Button
                type="button"
                disabled={submitting}
                isLoading={submitting}
                onClick={() => {
                  methods.handleSubmit(() => {
                    handleFormSubmit();
                  })();
                }}
              >
                ບັນທຶກ
              </Button>
            )}
          </div>
        </div>
      </FormRoot>
    );
  }

  // Modal variant: use compact layout (backward compatibility)
  return (
    <FormRoot<KPIFormValues>
      methods={methods}
      onSubmit={(vals) => handleFormSubmit(isEdit ? vals.status : "draft")}
      className="space-y-4"
    >
      <FormInput
        name="name"
        label="ຊື່ KPI"
        requiredMark
        placeholder="ຕົວຢ່າງ: ຍອດຂາຍລາຍເດືອນ"
      />
      <FormTextarea
        name="description"
        label="ຄຳອະທິບາຍ (ວັດແທກແນວໃດ)"
        placeholder="ຕົວຢ່າງ: ວັດຈາກຍອດຂາຍລວມຂອງພະແນກ"
      />
      <RHF.Controller
        name="level"
        control={methods.control}
        render={({ field }) => (
          <div className="space-y-2">
            <Label className="font-medium text-sm">
              ລະດັບ <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex flex-wrap gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="employee" id="level-employee" />
                <Label
                  htmlFor="level-employee"
                  className="cursor-pointer font-normal text-sm"
                >
                  ພະນັກງານ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="position" id="level-position" />
                <Label
                  htmlFor="level-position"
                  className="cursor-pointer font-normal text-sm"
                >
                  ຕຳແໜ່ງ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="department" id="level-department" />
                <Label
                  htmlFor="level-department"
                  className="cursor-pointer font-normal text-sm"
                >
                  ພະແນກ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="organization" id="level-organization" />
                <Label
                  htmlFor="level-organization"
                  className="cursor-pointer font-normal text-sm"
                >
                  ອົງກອນ
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}
      />
      <FormTextarea
        name="measurementMethod"
        label="ວິທີການວັດ"
        requiredMark
        placeholder="ຕົວຢ່າງ: ນັບຈາກຍອດຂາຍທັງໝົດຂອງພະແນກໃນແຕ່ລະເດືອນ"
      />
      <FormInput
        name="measurementSource"
        label="ແຫຼ່ງຂໍ້ມູນ"
        placeholder="ຕົວຢ່າງ: ຈາກ Checklist, ຈາກລະບົບ"
      />
      <RHF.Controller
        name="measurementPeriod"
        control={methods.control}
        render={({ field }) => (
          <div className="space-y-2">
            <Label className="font-medium text-sm">ຮອບການວັດ</Label>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex flex-wrap gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="monthly"
                  id="measurement-period-monthly"
                />
                <Label
                  htmlFor="measurement-period-monthly"
                  className="cursor-pointer font-normal text-sm"
                >
                  ເດືອນ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="quarterly"
                  id="measurement-period-quarterly"
                />
                <Label
                  htmlFor="measurement-period-quarterly"
                  className="cursor-pointer font-normal text-sm"
                >
                  ໄຕມາດ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yearly" id="measurement-period-yearly" />
                <Label
                  htmlFor="measurement-period-yearly"
                  className="cursor-pointer font-normal text-sm"
                >
                  ປີ
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}
      />
      <FormInput
        name="targetValue"
        label="ຄ່າເປົ້າໝາຍ"
        type="number"
        step="any"
        placeholder="ຕົວຢ່າງ: 100"
      />
      <RHF.Controller
        name="targetUnit"
        control={methods.control}
        render={({ field }) => (
          <div className="space-y-2">
            <Label className="font-medium text-sm">ຫົວໜ່ວຍຂອງຄ່າເປົ້າໝາຍ</Label>
            <RadioGroup
              value={field.value ?? ""}
              onValueChange={(val) => field.onChange(val || null)}
              className="flex flex-wrap gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="percent" id="target-unit-percent" />
                <Label
                  htmlFor="target-unit-percent"
                  className="cursor-pointer font-normal text-sm"
                >
                  ເປີເຊັນ (%)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="count" id="target-unit-count" />
                <Label
                  htmlFor="target-unit-count"
                  className="cursor-pointer font-normal text-sm"
                >
                  ຈຳນວນຄັ້ງ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="score" id="target-unit-score" />
                <Label
                  htmlFor="target-unit-score"
                  className="cursor-pointer font-normal text-sm"
                >
                  ຄະແນນ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="currency" id="target-unit-currency" />
                <Label
                  htmlFor="target-unit-currency"
                  className="cursor-pointer font-normal text-sm"
                >
                  ເງິນ
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="target-unit-none" />
                <Label
                  htmlFor="target-unit-none"
                  className="cursor-pointer font-normal text-sm"
                >
                  ບໍ່ມີຫົວໜ່ວຍ
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}
      />
      <FormCriteriaInput
        name="passCriteria"
        label="ກຳນົດຜ່ານ"
        targetUnit={methods.watch("targetUnit")}
        placeholder="ຕົວຢ່າງ: >= 80%"
      />
      <FormCriteriaInput
        name="failCriteria"
        label="ກຳນົດບໍ່ຜ່ານ"
        targetUnit={methods.watch("targetUnit")}
        placeholder="ຕົວຢ່າງ: < 60%"
      />
      <FormTextarea
        name="notes"
        label="ໝາຍເຫດ"
        placeholder="ຂໍ້ຄວາມເຕືອນ ຫຼື ສິ່ງທີ່ບໍ່ເອົາມານັບ"
      />
      {isEdit && (
        <div>
          <label htmlFor="status" className="mb-2 block font-medium text-sm">
            ສະຖານະ
          </label>
          <RHF.Controller
            name="status"
            control={methods.control}
            render={({ field }) => (
              <SimpleSelect
                value={field.value || "draft"}
                onValueChange={field.onChange}
                options={[
                  { value: "active", label: "ກຳລັງໃຊ້ງານ" },
                  { value: "draft", label: "ຮ່າງ" },
                  { value: "inactive", label: "ປິດການໃຊ້ງານ" },
                ]}
              />
            )}
          />
        </div>
      )}
      <div className="flex justify-between gap-2 pt-4">
        {isDev && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{kpiFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting} isLoading={submitting}>
            {isEdit ? "ບັນທຶກ" : "ສ້າງ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
